import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { X } from 'lucide-react';
import { addMixPanelEvent } from '../../store/actions';
import { AUDIO_QUALITY_RATED } from '../../constants/mixpanel-events';
import { resolveQualityRatingCopy } from '../../constants/qualityRatingCopy';
import { useGlobalStore } from '../../zustand-store/useGlobalStore';
import AudioQualityRating, { QualityRating } from '../audio-assets/AudioQualityRating';

const GAP = 10;          // distance between the anchor and the card
const MARGIN = 8;        // min distance from any viewport edge
const ANIM_MS = 180;     // enter/exit transition duration

// Shared "how was the generated audio?" prompt. Feature-agnostic: any surface
// fires requestQualityRating({ tool, source, anchor, ... }) on completion and
// this single mounted instance renders the card and emits AUDIO_QUALITY_RATED.
// When an `anchor` rect is supplied it sits next to that control, flipping and
// clamping to stay on screen (tooltip-like); otherwise it falls back to
// bottom-center. Fades/slides in and out.
const QualityRatingPrompt: React.FC = () => {
  const dispatch = useDispatch();
  const prompt = useGlobalStore((state) => state.qualityRatingPrompt);
  const dismissQualityRating = useGlobalStore((state) => state.dismissQualityRating);
  const optOutQualityRating = useGlobalStore((state) => state.optOutQualityRating);
  const storeOrgId = useGlobalStore((state) => state.orgId);
  const storeIntegrationId = useGlobalStore((state) => state.integrationId);
  const storeContentId = useGlobalStore((state) => state.contentId);

  const cardRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [rated, setRated] = useState(false);
  // `render` keeps the DOM around through the exit animation; `visible` drives
  // the opacity/transform transition.
  const [render, setRender] = useState(false);
  const [visible, setVisible] = useState(false);

  const { open, tool, source, meta = {}, copy, anchor, id } = prompt;

  // A fresh request resets the submitted state.
  useEffect(() => { setRated(false); }, [id]);

  // Mount on open; on close, animate out first, then unmount.
  useEffect(() => {
    if (open) {
      setRender(true);
      return;
    }
    setVisible(false);
    const t = setTimeout(() => setRender(false), ANIM_MS);
    return () => clearTimeout(t);
  }, [open]);

  // Once mounted (and positioned), flip to visible on the next frame so the
  // transition runs.
  useEffect(() => {
    if (!render || !open) return;
    const r = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(r);
  }, [render, open]);

  // Position next to the anchor once the card has a measured size. Re-runs after
  // submit too, since the card shrinks to just the thank-you note. Skipped while
  // closing so the card fades out where it is.
  useLayoutEffect(() => {
    if (!render || !open || !anchor || !cardRef.current) return;
    const { width: pw, height: ph } = cardRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left = anchor.left + anchor.width / 2 - pw / 2;
    left = Math.max(MARGIN, Math.min(left, vw - pw - MARGIN));

    // Prefer above the anchor; flip below if it would clip the top.
    let top = anchor.top - ph - GAP;
    if (top < MARGIN) top = anchor.top + anchor.height + GAP;
    top = Math.max(MARGIN, Math.min(top, vh - ph - MARGIN));

    setPos({ top, left });
  }, [render, open, anchor, id, rated]);

  if (!render) return null;

  const { title } = resolveQualityRatingCopy(tool, copy);

  // Studio surfaces pass contentId explicitly; others (dubbing) have none, so we
  // only fall back to the store's URL-derived id — never to a stale editor doc.
  const orgId = prompt.orgId || storeOrgId;
  const integrationId = prompt.integrationId || storeIntegrationId;
  const contentId = prompt.contentId || storeContentId || undefined;

  const handleRate = (rating: QualityRating) => {
    setRated(true);
    dispatch(addMixPanelEvent({
      orgId,
      integrationId,
      contentId,
      body: {
        eventName: AUDIO_QUALITY_RATED,
        eventData: { quality_rating: rating, tool, source, ...meta },
      },
    }));
    // Leave the thank-you note visible briefly, then close (which animates out).
    setTimeout(() => dismissQualityRating(), 1600);
  };

  // Anchored placement needs a measure pass first; stay invisible until `pos` is
  // ready so it never flashes in the wrong spot. Unanchored → bottom-center.
  const positionStyle: React.CSSProperties = anchor
    ? { top: pos?.top ?? 0, left: pos?.left ?? 0 }
    : { bottom: 74, left: '50%' };

  const shown = visible && (!anchor || !!pos);
  const baseTranslate = anchor ? '' : 'translateX(-50%) ';
  const transform = `${baseTranslate}translateY(${shown ? '0' : '6px'})`;

  return (
    <div
      ref={cardRef}
      role="dialog"
      style={{
        position: 'fixed',
        background: '#FFFFFF',
        border: '1px solid #E5E7EB',
        borderRadius: 12,
        boxShadow: '0 10px 40px rgba(0,0,0,0.18)',
        padding: '12px 14px',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 14,
        // Intrinsic width so the card keeps its natural (one-line) size even when
        // anchored near the right edge — a fixed box sized only by `left` would
        // otherwise shrink to the space remaining to the edge and wrap. Capped for
        // small screens.
        width: 'max-content',
        maxWidth: 'calc(100vw - 16px)',
        fontFamily: 'inherit',
        opacity: shown ? 1 : 0,
        transform,
        transition: `opacity ${ANIM_MS}ms ease, transform ${ANIM_MS}ms ease`,
        pointerEvents: shown ? 'auto' : 'none',
        ...positionStyle,
      }}
    >
      {rated ? (
        <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
          Thanks for the feedback! 🙌
        </span>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{title}</span>
            <AudioQualityRating key={id} onRate={handleRate} />
            <button
              type="button"
              onClick={() => optOutQualityRating()}
              style={{
                alignSelf: 'flex-start',
                marginTop: 6,
                padding: 0,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 11,
                color: '#9CA3AF',
                textDecoration: 'underline',
              }}
            >
              Don't show this again
            </button>
          </div>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => dismissQualityRating()}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              display: 'flex',
              alignItems: 'center',
              color: '#9CA3AF',
            }}
          >
            <X size={15} />
          </button>
        </>
      )}
    </div>
  );
};

export default QualityRatingPrompt;
